import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import WeightInput, { fromCanonicalLb, toCanonicalLb } from "@/components/app/WeightInput";
import UnitInput from "@/components/app/UnitInput";
import { valueForSet } from "@/lib/scheme";
import {
  toCanonicalMeters, fromCanonicalMeters,
  toCanonicalInches, fromCanonicalInches,
} from "@/lib/units";

export default function LogSetButton({ mv, program, dayIdx, blockId, blockType, athleteId, open, setOpen, trigger, onLogged }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  // Default the unit for this set: prefer the movement's recorded unit (so logged sets
  // mirror how the program was written), else the user's preference.
  const startUnit = mv.weight_unit || userUnit;
  const startDistanceUnit = mv.distance_unit || "m";
  const startHeightUnit = mv.height_unit || "in";
  // Local display unit for the Time field on this log entry — canonical
  // storage is always seconds. Default to Min when the movement's target
  // is already ≥ 120s so athletes typing "5" mean minutes, not seconds.
  const [timeUnit, setTimeUnit] = useState(() => (mv.time_seconds ?? 0) >= 120 ? "m" : "s");

  // Has any scheme been prescribed for this movement?
  const hasScheme = !!(mv.reps_scheme?.length || mv.weight_scheme?.length
    || mv.distance_scheme?.length || mv.calories_scheme?.length || mv.height_scheme?.length);

  // Returns the prescribed value (or fallback) for a given 1-indexed set.
  // `weight`/`distance`/`height` schemes are stored canonical (lb / m / in)
  // and converted to the chosen display unit here.
  const prefillFor = (n) => {
    const reps = mv.reps_scheme?.length ? valueForSet(mv.reps_scheme, n, mv.reps) : (mv.reps ?? "");
    const weightLb = mv.weight_scheme?.length ? valueForSet(mv.weight_scheme, n, mv.weight) : mv.weight;
    const distM = mv.distance_scheme?.length ? valueForSet(mv.distance_scheme, n, mv.distance_meters) : mv.distance_meters;
    const heightIn = mv.height_scheme?.length ? valueForSet(mv.height_scheme, n, mv.height_inches) : mv.height_inches;
    const cals = mv.calories_scheme?.length ? valueForSet(mv.calories_scheme, n, mv.calories) : (mv.calories ?? "");
    return {
      reps: reps ?? "",
      weight: fromCanonicalLb(weightLb, startUnit) ?? "",
      distance: fromCanonicalMeters(distM, startDistanceUnit) ?? "",
      height: fromCanonicalInches(heightIn, startHeightUnit) ?? "",
      calories: cals ?? "",
    };
  };

  const [set, setSet] = useState(() => {
    const p = prefillFor(1);
    return {
      set_number: 1,
      reps: p.reps,
      weight: p.weight,
      weight_unit: startUnit,
      time_seconds: mv.time_seconds ?? "",
      distance: p.distance,
      distance_unit: startDistanceUnit,
      height: p.height,
      height_unit: startHeightUnit,
      calories: p.calories,
      rpe: mv.rpe ?? "",
      notes: "",
    };
  });

  // Whenever the user bumps `set_number` (or the dialog opens), re-prefill the
  // scheme-driven fields. We don't overwrite values the user has typed during
  // this session that *aren't* scheme-controlled (notes, rpe, time_seconds).
  useEffect(() => {
    if (!open || !hasScheme) return;
    setSet((s) => {
      const p = prefillFor(s.set_number);
      const patch = {};
      if (mv.reps_scheme?.length) patch.reps = p.reps;
      if (mv.weight_scheme?.length) patch.weight = p.weight;
      if (mv.distance_scheme?.length) patch.distance = p.distance;
      if (mv.height_scheme?.length) patch.height = p.height;
      if (mv.calories_scheme?.length) patch.calories = p.calories;
      return { ...s, ...patch };
    });
    // We deliberately only react to set_number + open so the user can override
    // a prefill mid-session and have it persist until the next set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set.set_number, open]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      await api.post("/workout-logs", {
        program_id: program.id,
        movement_id: mv.id,
        movement_name: mv.name,
        day_of_week: dayIdx,
        set_number: Number(set.set_number) || 1,
        reps: num(set.reps),
        weight: toCanonicalLb(set.weight, set.weight_unit),
        weight_unit: set.weight_unit || "lb",
        time_seconds: num(set.time_seconds),
        distance_meters: toCanonicalMeters(set.distance, set.distance_unit),
        distance_unit: set.distance_unit || "m",
        height_inches: toCanonicalInches(set.height, set.height_unit),
        height_unit: set.height_unit || "in",
        calories: num(set.calories),
        rpe: num(set.rpe),
        notes: set.notes || null,
        block_rep_scheme: mv.reps_scheme && mv.reps_scheme.length ? mv.reps_scheme : null,
      });
      toast.success(`Set ${set.set_number} logged`);
      setOpen(false);
      setSet((s) => ({ ...s, set_number: (Number(s.set_number) || 1) + 1, notes: "" }));
      onLogged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="w-full rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white h-11" data-testid={`log-set-${mv.id}`}>
            <CheckCircle size={16} className="mr-2" /> Log a set
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-heading">Log set — {mv.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="log-set-form">
          {hasScheme && (
            <div className="rounded-xl bg-terracotta/10 border border-terracotta/30 px-3 py-2 flex items-center gap-2" data-testid="log-set-scheme-banner">
              <Lightning size={14} weight="fill" className="text-terracotta shrink-0" />
              <div className="text-xs text-ink">
                <span className="font-semibold">Scheme workout</span>
                <span className="text-ink-3"> — fields auto-fill from set {set.set_number}. Change set # to step through.</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="hs-label">Set #</Label>
              <Input type="number" min="1" value={set.set_number} onChange={(e) => setSet({ ...set, set_number: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="log-set-number" />
            </div>
            <div>
              <Label className="hs-label">Reps</Label>
              <Input type="number" min="0" value={set.reps} onChange={(e) => setSet({ ...set, reps: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="log-set-reps" />
            </div>
            <WeightInput
              value={set.weight}
              unit={set.weight_unit}
              onChange={(v, u) => setSet({ ...set, weight: v, weight_unit: u })}
              testId="log-set-weight"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="hs-label">Time ({timeUnit === "m" ? "min" : "s"})</Label>
                <div className="inline-flex rounded-full border border-line overflow-hidden text-[10px] font-bold uppercase tracking-[0.14em]">
                  <button
                    type="button"
                    onClick={() => setTimeUnit("s")}
                    className={`px-2 py-0.5 ${timeUnit === "s" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
                    data-testid="log-set-time-unit-s"
                  >S</button>
                  <button
                    type="button"
                    onClick={() => setTimeUnit("m")}
                    className={`px-2 py-0.5 ${timeUnit === "m" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
                    data-testid="log-set-time-unit-m"
                  >Min</button>
                </div>
              </div>
              <Input
                type="number"
                step={timeUnit === "m" ? "0.5" : "1"}
                min="0"
                max={timeUnit === "m" ? "120" : "7200"}
                value={
                  set.time_seconds === "" || set.time_seconds == null
                    ? ""
                    : timeUnit === "m"
                      ? Number((Number(set.time_seconds) / 60).toFixed(2))
                      : set.time_seconds
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { setSet({ ...set, time_seconds: "" }); return; }
                  const v = Number(raw);
                  if (Number.isNaN(v)) return;
                  // Clamp to 120 minutes (7200 s) regardless of unit.
                  const secs = Math.min(7200, Math.max(0, timeUnit === "m" ? Math.round(v * 60) : Math.round(v)));
                  setSet({ ...set, time_seconds: secs });
                }}
                className="mt-2 h-11 rounded-xl font-data"
                data-testid="log-set-time"
              />
            </div>
            <div>
              <Label className="hs-label">Calories</Label>
              <Input type="number" min="0" value={set.calories} onChange={(e) => setSet({ ...set, calories: e.target.value })} className="mt-2 h-11 rounded-xl font-data" data-testid="log-set-calories" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <UnitInput
              kind="distance"
              value={set.distance}
              unit={set.distance_unit}
              onChange={(v, u) => setSet({ ...set, distance: v, distance_unit: u })}
              testId="log-set-distance"
            />
            <UnitInput
              kind="height"
              value={set.height}
              unit={set.height_unit}
              onChange={(v, u) => setSet({ ...set, height: v, height_unit: u })}
              testId="log-set-height"
            />
          </div>
          <div>
            <Label className="hs-label">RPE</Label>
            <Input type="number" step="0.5" min="0" max="10" value={set.rpe} onChange={(e) => setSet({ ...set, rpe: e.target.value })} className="mt-2 h-11 rounded-xl font-data" />
          </div>
          <div>
            <Label className="hs-label">Notes</Label>
            <Textarea rows={2} value={set.notes} onChange={(e) => setSet({ ...set, notes: e.target.value })} className="mt-2 rounded-xl" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="submit-log-set">{busy ? "Saving…" : "Log set"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
